import {useEffect, useMemo, useState} from 'react';

import {Api} from 'apiClient/Api';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';

/*
 * Lesende Labelwin-Stammdaten für die Auswahlfelder (`/Labelwin/...`). Wie bei den Aufgaben liegen
 * die Typen lokal, weil der erzeugte Client die Endpunkte noch nicht kennt.
 */

/** Ein Kunde aus den Adressstammdaten. */
export interface LabelwinCustomer {
    customerId: number;
    name: string | null;
    name2: string | null;
    street: string | null;
    postalCode: string | null;
    city: string | null;
    phone: string | null;
    searchTerm: string | null;
    debtorNumber: string | null;
}

/** Ein Ansprechpartner eines Kunden. */
export interface LabelwinContact {
    contactId: number;
    customerId: number;
    name: string | null;
    phone: string | null;
    mail: string | null;
    function: string | null;
    salutation: string | null;
}

/** Eine Anlage eines Kunden. */
export interface LabelwinSystem {
    systemId: number;
    customerId: number;
    name: string | null;
    supportId: string | null;
    contractNumber: string | null;
    info: string | null;
}

/** Ein Eintrag einer Auswahlliste (Tätigkeitsart, Stundenart, Auftragsart, Priorität). */
export interface LabelwinLookup {
    id: number;
    name: string | null;
    order: number | null;
    priority: number | null;
}

/** Die Auswahllisten, die der Server anbietet. */
export type LookupList = 'Activities' | 'HourTypes' | 'OrderTypes' | 'Priorities' | 'OrderStates';

/**
 * Zwischenspeicher je Liste für die Lebensdauer der Seite.
 *
 * Die Listen ändern sich praktisch nie, hängen aber an jedem Dialog — ohne das holte sie jedes
 * Öffnen erneut. Der Server puffert zusätzlich; hier geht es nur darum, nicht bei jedem Mounten
 * eine Anfrage abzusetzen. Gespeichert wird das Versprechen, damit zwei gleichzeitig geöffnete
 * Dialoge nicht zweimal laden.
 */
const lookupCache = new Map<LookupList, Promise<LabelwinLookup[]>>();

/** Lädt eine Auswahlliste (einmal je Liste und Seitenaufruf). */
export const loadLookup = (api: Api<unknown>, list: LookupList): Promise<LabelwinLookup[]> => {
    const cached = lookupCache.get(list);
    if (cached) return cached;

    const pending = settingsApi(api)
        .get<LabelwinLookup[]>(`/Labelwin/Lookups/${list}`)
        .then((r) => r.data)
        .catch((e) => {
            // Ein Fehlschlag darf nicht dauerhaft hängen bleiben: sonst bliebe die Auswahl auch
            // nach einem behobenen Ausfall leer, bis jemand die Seite neu lädt.
            lookupCache.delete(list);
            throw e;
        });

    lookupCache.set(list, pending);
    return pending;
};

/**
 * Eine Auswahlliste als Hook.
 *
 * Schlägt der Abruf fehl, bleibt die Liste leer — das Formular zeigt dann kein Auswahlfeld statt
 * einer Fehlermeldung. Die Felder sind durchweg optional; ohne Auswahl greifen die Vorgaben von
 * LabelConnect.
 */
export const useLookup = (api: Api<unknown>, list: LookupList): LabelwinLookup[] => {
    const [values, setValues] = useState<LabelwinLookup[]>([]);

    useEffect(() => {
        let active = true;
        loadLookup(api, list)
            .then((data) => { if (active) setValues(data); })
            .catch(() => { if (active) setValues([]); });

        return () => { active = false; };
    }, [api, list]);

    return values;
};

/**
 * Sucht Kunden über Name, Suchwort, Ort oder Kundennummer.
 *
 * Der Server verlangt mindestens zwei Zeichen — kürzere Eingaben liefern hier eine leere Liste,
 * statt in ein 400 zu laufen.
 */
export const useCustomerSearch = (api: Api<unknown>, term: string): {
    customers: LabelwinCustomer[];
    loading: boolean;
} => {
    const http = useMemo(() => settingsApi(api), [api]);
    const [customers, setCustomers] = useState<LabelwinCustomer[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const trimmed = term.trim();
        if (trimmed.length < 2) {
            setCustomers([]);
            return;
        }

        let active = true;
        setLoading(true);

        // Kurz warten statt bei jedem Tastendruck zu fragen: die Suche geht über eine
        // Adressdatei mit zehntausenden Sätzen.
        const timer = window.setTimeout(() => {
            http.get<LabelwinCustomer[]>('/Labelwin/Customers', {suche: trimmed, limit: 50})
                .then((r) => { if (active) setCustomers(r.data); })
                .catch(() => { if (active) setCustomers([]); })
                .finally(() => { if (active) setLoading(false); });
        }, 300);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [http, term]);

    return {customers, loading};
};

/** Beschriftung eines Kunden für Auswahlfelder. */
export const customerLabel = (customer: LabelwinCustomer): string =>
    [`${customer.customerId}`, customer.name, customer.city].filter(Boolean).join(' · ');
